import {
  mxCellMarker,
  mxClient,
  mxConnectionHandler,
  mxConstants,
  mxEvent,
  mxEventObject,
  mxEventSource,
  mxGraph,
  mxGraphHandler,
  mxGraphView,
  mxRectangle,
  mxUndoManager,
  mxUtils,
} from './mxGraphFactory';
import {Graph} from './Graph';
import {createSvgImage} from './helper';

export const Editor = function (editable) {
    mxEventSource.call(this);

    this.graph = new Graph();
    this.editable = editable !== undefined ? editable : true;
    this.undoManager = this.createUndoManager();

    const graphChangeListener = (sender, eventObject) => {
        const edit = (eventObject != null) ? eventObject.getProperty('edit') : null;
        if (edit == null || !edit.ignoreEdit) {
            this.setModified(true);
        }
    };

    this.graph.getModel().addListener(mxEvent.CHANGE, mxUtils.bind(this, function () {
        graphChangeListener.apply(this, arguments);
    }));

    this.graph.resetViewOnRootChange = false;
    this.graph.editable = this.editable;
};

/**
 * Specifies the image URL to be used for the transparent background.
 */
Editor.ctrlKey = (mxClient.IS_MAC) ? 'Cmd' : 'Ctrl';

/**
 * Editor inherits from mxEventSource
 */
mxUtils.extend(Editor, mxEventSource);

Editor.prototype.originalNoForeignObject = mxClient.NO_FO;
Editor.prototype.enabled = true;
Editor.prototype.modified = false;

Editor.prototype.resetGraph = function () {
    this.graph.gridEnabled = true;
    this.graph.graphHandler.guidesEnabled = this.editable;
    this.graph.edgeLabelsMovable = false;
    this.graph.setTooltips(this.editable);
    this.graph.setConnectable(this.editable);
    this.graph.cellsEditable = this.editable;
    this.graph.cellsDeletable = this.editable;
    this.graph.cellsLocked = !this.editable;
    this.graph.cellsMovable = this.editable;
    this.graph.cellsResizable = this.editable;
    this.graph.cellsBendable = this.editable;
    this.graph.cellsCloneable = this.editable;
    this.graph.scrollbars = this.graph.defaultScrollbars;
    this.graph.background = this.graph.defaultGraphBackground;
    this.graph.pageScale = mxGraph.prototype.pageScale;
    this.graph.pageFormat = mxGraph.prototype.pageFormat;
    this.graph.currentScale = 1;
    this.graph.currentTranslate.x = 0;
    this.graph.currentTranslate.y = 0;
    this.updateGraphComponents();
    this.graph.view.setScale(1);
};

Editor.prototype.initializeWorkflow = function (workflow) {
    const graph = this.graph;
    graph.setWorkflow(workflow);
    this.undoManager.clear();
    this.setModified(false);

    const bounds = graph.getGraphBounds();
    const width = bounds.width * graph.view.scale;
    const height = bounds.height * graph.view.scale;
    graph.container.scrollTop = Math.floor(Math.max(0, bounds.y - Math.max(20, (graph.container.clientHeight - height) / 4)));
    graph.container.scrollLeft = Math.floor(Math.max(0, bounds.x - Math.max(0, (graph.container.clientWidth - width) / 10)));
}

Editor.prototype.updateGraphComponents = function () {
    const graph = this.graph;

    if (graph.container != null) {
        graph.view.validateBackground();
        graph.container.style.overflow = (graph.scrollbars) ? 'auto' : 'hidden';

        this.fireEvent(new mxEventObject('updateGraphComponents'));
    }
};

Editor.prototype.setModified = function (value) {
    this.modified = value;
    this.graph.fireEvent(new mxEventObject('graphModified', 'modified', value));
};

Editor.prototype.createUndoManager = function () {
    const graph = this.graph;
    const undoManager = new mxUndoManager();

    graph.getModel().addListener(mxEvent.UNDO, (sender, evt) => undoManager.undoableEditHappened(evt.getProperty('edit')));
    graph.getView().addListener(mxEvent.UNDO, (sender, evt) => undoManager.undoableEditHappened(evt.getProperty('edit')));

    const undoHandler = (sender, evt) => {
        const cellsForChanges = graph.getSelectionCellsForChanges(evt.getProperty('edit').changes);
        const cells = [];

        const model = graph.getModel();
        cellsForChanges.forEach(cellsForChange => {
            if ((model.isVertex(cellsForChange) || model.isEdge(cellsForChange)) && graph.view.getState(cellsForChange) != null) {
                cells.push(cellsForChange);
            }
        });

        graph.setSelectionCells(cells);
    };

    undoManager.addListener(mxEvent.UNDO, undoHandler);
    undoManager.addListener(mxEvent.REDO, undoHandler);

    return undoManager;
};

Editor.prototype.destroy = function () {
    if (this.graph != null) {
        this.graph.destroy();
        this.graph = null;
    }
};

(function () {
    mxGraphView.prototype.validateBackgroundPage = function () {
        const graph = this.graph;

        if (graph.container != null && !graph.transparentBackground) {
            this.validateBackgroundStyles();
        }
    };

    mxGraphView.prototype.validateBackgroundStyles = function () {
        const graph = this.graph;
        const color = (graph.background == null || graph.background === mxConstants.NONE) ? graph.defaultPageBackgroundColor : graph.background;
        const gridColor = (color != null && this.gridColor !== color.toLowerCase()) ? this.gridColor : '#ffffff';
        let image = 'none';
        let position = '';

        if (graph.isGridEnabled()) {
            image = 'url(\'' + this.createSvgGrid(gridColor).src + '\')';
            const phase = graph.gridSize * this.scale * this.gridTasks;

            position = -Math.round(phase - mxUtils.mod(this.translate.x * this.scale, phase)) + 'px ' +
                -Math.round(phase - mxUtils.mod(this.translate.y * this.scale, phase)) + 'px';
        }

        let canvas = graph.view.canvas;

        if (canvas.ownerSVGElement != null) {
            canvas = canvas.ownerSVGElement;
        }

        graph.container.className = 'geDiagramContainer';
        canvas.style.backgroundPosition = position;
        canvas.style.backgroundColor = color;
        canvas.style.backgroundImage = image;
    };

    mxGraphView.prototype.createSvgGrid = function (color) {
        let tmp = this.graph.gridSize * this.scale;

        while (tmp < this.minGridSize) {
            tmp *= 2;
        }

        const tmp2 = this.gridTasks * tmp;

        const d = [];

        for (let i = 1; i < this.gridTasks; i++) {
            const tmp3 = i * tmp;
            d.push('M 0 ' + tmp3 + ' L ' + tmp2 + ' ' + tmp3 + ' M ' + tmp3 + ' 0 L ' + tmp3 + ' ' + tmp2);
        }

        const size = tmp2;
        return createSvgImage(size, size, '<defs><pattern id="grid" width="' + tmp2 + '" height="' + tmp2 + '" patternUnits="userSpaceOnUse">' +
            '<path d="' + d.join(' ') + '" fill="none" stroke="' + color + '" opacity="0.2" stroke-width="1"/>' +
            '<path d="M ' + tmp2 + ' 0 L 0 0 0 ' + tmp2 + '" fill="none" stroke="' + color + '" stroke-width="1"/>' +
            '</pattern></defs><rect width="100%" height="100%" fill="url(#grid)"/>');
    };

    const mxGraphPanGraph = mxGraph.prototype.panGraph;
    mxGraph.prototype.panGraph = function (dx, dy) {
        mxGraphPanGraph.apply(this, arguments);

        if (this.shiftPreview1 != null) {
            let canvas = this.view.canvas;

            if (canvas.ownerSVGElement != null) {
                canvas = canvas.ownerSVGElement;
            }

            const phase = this.gridSize * this.view.scale * this.view.gridTasks;
            canvas.style.backgroundPosition = -Math.round(phase - mxUtils.mod(this.view.translate.x * this.view.scale + dx, phase)) + 'px ' +
                -Math.round(phase - mxUtils.mod(this.view.translate.y * this.view.scale + dy, phase)) + 'px';
        }
    };

    // Disables removing relative children from parents
    const mxGraphHandlerShouldRemoveCellsFromParent = mxGraphHandler.prototype.shouldRemoveCellsFromParent;
    mxGraphHandler.prototype.shouldRemoveCellsFromParent = function (parent, cells, evt) {
        for (let i = 0; i < cells.length; i++) {
            if (this.graph.getModel().isVertex(cells[i])) {
                const geo = this.graph.getCellGeometry(cells[i]);

                if (geo != null && geo.relative) {
                    return false;
                }
            }
        }

        return mxGraphHandlerShouldRemoveCellsFromParent.apply(this, arguments);
    };

    // Overrides to ignore hotspot only for target terminal
    const mxConnectionHandlerCreateMarker = mxConnectionHandler.prototype.createMarker;
    mxConnectionHandler.prototype.createMarker = function () {
        const marker = mxConnectionHandlerCreateMarker.apply(this, arguments);

        marker.intersects = mxUtils.bind(this, function (state, evt) {
            if (this.isConnecting()) {
                return true;
            }

            return mxCellMarker.prototype.intersects.apply(marker, arguments);
        });

        return marker;
    };

    // Fits the number of background pages to the graph
    mxGraphView.prototype.getBackgroundPageBounds = function () {
        const gb = this.getGraphBounds();

        // Computes unscaled, untranslated graph bounds
        const x = (gb.width > 0) ? gb.x / this.scale - this.translate.x : 0;
        const y = (gb.height > 0) ? gb.y / this.scale - this.translate.y : 0;
        const w = gb.width / this.scale;
        const h = gb.height / this.scale;

        const fmt = this.graph.pageFormat;
        const ps = this.graph.pageScale;

        const pw = fmt.width * ps;
        const ph = fmt.height * ps;

        const x0 = Math.floor(Math.min(0, x) / pw);
        const y0 = Math.floor(Math.min(0, y) / ph);
        const xe = Math.ceil(Math.max(1, x + w) / pw);
        const ye = Math.ceil(Math.max(1, y + h) / ph);

        const rows = xe - x0;
        const cols = ye - y0;

        return new mxRectangle(this.scale * (this.translate.x + x0 * pw), this.scale *
            (this.translate.y + y0 * ph), this.scale * rows * pw, this.scale * cols * ph);
    };

    // Selects ancestors before descendants
    const graphHandlerGetInitialCellForEvent = mxGraphHandler.prototype.getInitialCellForEvent;
    mxGraphHandler.prototype.getInitialCellForEvent = function (me) {
        const model = this.graph.getModel();
        const psel = model.getParent(this.graph.getSelectionCell());
        let cell = graphHandlerGetInitialCellForEvent.apply(this, arguments);
        let parent = model.getParent(cell);

        if (psel == null || (psel !== cell && psel !== parent)) {
            while (!this.graph.isCellSelected(cell) && !this.graph.isCellSelected(parent) && model.isVertex(parent)) {
                cell = parent;
                parent = this.graph.getModel().getParent(cell);
            }
        }

        return cell;
    };

    // Selection is delayed to mouseup if ancestor is selected
    const graphHandlerIsDelayedSelection = mxGraphHandler.prototype.isDelayedSelection;
    mxGraphHandler.prototype.isDelayedSelection = function (cell, me) {
        let result = graphHandlerIsDelayedSelection.apply(this, arguments);

        if (!result) {
            const model = this.graph.getModel();
            let parent = model.getParent(cell);

            while (parent != null) {
                // Inconsistency for unselected parent swimlane is intended for easier moving
                // of stack layouts where the container title section is too far away
                if (this.graph.isCellSelected(parent) && model.isVertex(parent)) {
                    result = true;
                    break;
                }

                parent = model.getParent(parent);
            }
        }

        return result;
    };
})();
